import { type ApolloError, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  type ContractType,
  GetEmployeeDocument,
  useCreateEmployeeContractMutation,
  useDeleteEmployeeContractMutation,
  useUpdateEmployeeContractMutation,
} from '@/common/graphql';
import type { EmployeeContractHistoryType } from '@/common/types';
import { ApiUtils, StringUtils } from '@/common/utils';

interface ContractHistoryPersistence {
  employeeId: string;
}

export const useContractHistoryPersistence = ({ employeeId }: ContractHistoryPersistence) => {
  const client = useApolloClient();
  const onCompleted = () => {
    toast.success();
    client.refetchQueries({
      include: [GetEmployeeDocument],
    });
  };
  const onError = (error: ApolloError) => ApiUtils.handleApiError(error);

  const [createRecord] = useCreateEmployeeContractMutation({
    onCompleted,
    onError,
  });
  const [updateRecord] = useUpdateEmployeeContractMutation({
    onCompleted,
    onError,
  });
  const [deleteRecord] = useDeleteEmployeeContractMutation({
    onCompleted,
    onError,
  });

  const onContractHistoryCreate = useCallback(
    async (formData: EmployeeContractHistoryType) => {
      const { data } = await createRecord({
        variables: {
          input: {
            employeeId,
            companyId: formData.companyId || '',
            contractType: formData.type as ContractType,
            effectiveDate: formData.effectiveDateStart,
            effectiveDateUntil: StringUtils.emptyToNull(formData.effectiveDateEnd),
            employeeCode: formData.employeeCode ? formData.employeeCode : null,
            onSecondment: formData.isOnSecondment,
            isMain: formData.isMain,
            isPreAcquisition: formData.isPreAcquisition,
          },
        },
      });

      return data?.createEmployeeContract !== undefined;
    },
    [createRecord, employeeId],
  );

  const onContractHistoryUpdate = useCallback(
    async (formData: EmployeeContractHistoryType) => {
      const { data } = await updateRecord({
        variables: {
          input: {
            companyId: formData.companyId || '',
            contractType: formData.type as ContractType,
            effectiveDate: formData.effectiveDateStart,
            effectiveDateUntil: StringUtils.emptyToNull(formData.effectiveDateEnd),
            employeeCode: formData.employeeCode ? formData.employeeCode : null,
            onSecondment: formData.isOnSecondment,
            id: formData.id,
            isMain: formData.isMain,
            isPreAcquisition: formData.isPreAcquisition,
          },
        },
      });

      return data?.updateEmployeeContract !== undefined;
    },
    [updateRecord],
  );

  const onContractHistoryRemove = useCallback(
    async (id: string) => {
      await deleteRecord({
        variables: { id: id },
      });
    },
    [deleteRecord],
  );

  return {
    onContractHistoryCreate,
    onContractHistoryUpdate,
    onContractHistoryRemove,
  };
};
